import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Video, Trash2, Search, CalendarClock, Pencil } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { OnlineStatusBadge } from '@/components/cameras/OnlineStatusBadge'
import { CameraEditSheet } from '@/components/cameras/CameraEditSheet'
import { camerasApi, adminApi } from '@/api/endpoints'
import { avatarColor } from '@/lib/utils'
import { ALL_CAMERAS_SCHEMA } from '@/lib/urlSchemas'
import { useUrlState } from '@/hooks/useUrlState'
import type { Camera } from '@/types'
import { masterPasswordRefusal } from '@/lib/deleteProtection'

// Debounce hook for search (same pattern as CamerasPage's local helper).
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debounced
}

/**
 * Superuser-only "All Cameras" admin page (#095).
 * Lists every camera across all owners (via GET /cameras/?scope=all), with an owner
 * column, owner filter, search, and per-row delete + "view events" drill-down.
 * Route is gated by <SuperuserRoute>; the backend independently enforces is_superuser.
 */
export function AllCamerasPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  // Filters live in the URL (shareable, refresh-safe, restored on back-navigation).
  // deleteTarget/editId stay local — ephemeral dialog state, deliberately not in the URL.
  const [filters, setFilters] = useUrlState(ALL_CAMERAS_SCHEMA)
  const ownerId = filters.owner
  const setOwnerId = (v: string) => setFilters({ owner: v })

  // Free-text search is DEBOUNCED into the URL, exactly as CamerasPage does it.
  // Writing on every keystroke would fire one history.replaceState + one scroll reset
  // + one new location.key per character — Safari throws SecurityError past ~100
  // replaceState calls in 30s (Chrome throttles), and each new key churns the 30-entry
  // snapshot LRU. One `replace` per typing pause instead.
  const [search, setSearchInput] = useState(filters.q)
  const debouncedSearch = useDebounce(search, 300)
  useEffect(() => {
    if (debouncedSearch !== filters.q) setFilters({ q: debouncedSearch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])
  const setSearch = (v: string) => setSearchInput(v)

  const [deleteTarget, setDeleteTarget] = useState<Camera | null>(null)
  // #373 — the delete carries the master password typed in the dialog.
  const { t: tc } = useTranslation('common')
  const [masterPassword, setMasterPassword] = useState('')
  const masterPasswordRef = useRef('')
  const [masterPasswordError, setMasterPasswordError] = useState<string | null>(null)
  const typeMasterPassword = (v: string) => { masterPasswordRef.current = v; setMasterPassword(v); setMasterPasswordError(null) }
  const resetMasterPassword = () => { masterPasswordRef.current = ''; setMasterPassword(''); setMasterPasswordError(null) }
  const [editId, setEditId] = useState<number | null>(null)

  const { data: users = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers(),
  })

  const { data: cameras = [], isLoading } = useQuery({
    queryKey: ['all-cameras', ownerId, filters.q],
    queryFn: () => camerasApi.getAllAdmin(ownerId === 'all' ? undefined : Number(ownerId), filters.q || undefined),
  })

  // The slim admin list omits settings fields, so re-fetch the full camera to edit it.
  const { data: editCamera } = useQuery({
    queryKey: ['camera', editId],
    queryFn: () => camerasApi.getById(editId as number),
    enabled: editId != null,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => camerasApi.delete(id, masterPasswordRef.current),  // #373
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-cameras'] })
      toast.success('Camera deleted')
      setDeleteTarget(null)
      resetMasterPassword()
    },
    onError: (err: unknown) => {
      const refusal = masterPasswordRefusal(err)
      if (refusal) {  // #373 — the dialog stays open with the server's sentence
        setMasterPasswordError(refusal.code === 'master_password_locked' && refusal.retryAfter
          ? `${refusal.detail} ${tc('deleteProtection.retryIn', { seconds: refusal.retryAfter })}`
          : refusal.detail || tc('deleteProtection.refused'))
        return
      }
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed'
      toast.error(detail)
    },
  })

  return (
    <div className="space-y-6">
      {/* #126 — nav bar shows "All Cameras"; drop the redundant title/subtitle. */}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search name, IMEI, location" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={ownerId} onValueChange={setOwnerId}>
          <SelectTrigger className="w-[240px]"><SelectValue placeholder="All owners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {users.map(u => (
              <SelectItem key={u.id} value={String(u.id)}>@{u.username} · {u.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-body font-semibold flex items-center gap-2">
            <Video className="h-4 w-4 text-muted-foreground" />
            {cameras.length} camera{cameras.length === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : cameras.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No cameras found</div>
          ) : (
            <div>
              {cameras.map((c, idx) => (
                <div key={c.id} className={`flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors ${idx > 0 ? 'border-t border-border/40' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      <OnlineStatusBadge isOnline={c.is_online} lastSeen={c.last_seen} />
                    </div>
                    <p className="text-xs text-muted-foreground truncate">IMEI {c.imei}{c.location ? ` · ${c.location}` : ''}</p>
                  </div>

                  {/* Owner */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0 w-52 min-w-0">
                    <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${avatarColor(c.user_username || '?')}`}>
                      {(c.user_username || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">@{c.user_username || '—'}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{c.user_email || '—'}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="View events"
                      onClick={() =>
                        navigate(`/events?camera=${c.id}&category=${c.default_event_category ?? 'animal'}`, {
                          state: {
                            from: {
                              pathname: '/all-cameras',
                              search: window.location.search,
                              labelKey: 'nav.allCameras',
                            },
                          },
                        })
                      }
                    >
                      <CalendarClock className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit camera" onClick={() => setEditId(c.id)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/8" title="Delete camera" onClick={() => { resetMasterPassword(); setDeleteTarget(c) }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) { setDeleteTarget(null); resetMasterPassword() } }}
        title="Delete camera"
        description={`Delete "${deleteTarget?.name}" (owner @${deleteTarget?.user_username})? This removes all its events, media, and S3 files.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        input={{ label: tc('deleteProtection.masterPassword'), value: masterPassword, onChange: typeMasterPassword, error: masterPasswordError }}
      />

      {editCamera && (
        <CameraEditSheet
          camera={editCamera}
          open={editId != null}
          onOpenChange={(open) => {
            if (!open) {
              setEditId(null)
              // CameraEditSheet invalidates the standard camera keys on save; refresh the
              // admin list too so an edit to another user's camera shows immediately.
              qc.invalidateQueries({ queryKey: ['all-cameras'] })
            }
          }}
        />
      )}
    </div>
  )
}
